<template>
  <div class="min-h-screen px-6 py-8 md:px-10">
    <div class="w-full space-y-6">
      <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 class="mt-2 text-3xl text-gray-900">Novo Centro de Custo</h1>
          <p class="mt-2 max-w-2xl text-sm text-gray-500">Cadastre um centro de custo.</p>
        </div>

        <div class="flex flex-col gap-3 sm:flex-row">
          <button
            type="button"
            class="icon-anim-trigger--back group flex h-11 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-5 font-medium text-gray-700 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-gray-300 hover:bg-gray-50 hover:shadow-md"
            @click="voltar"
          >
            <svg class="icon-anim icon-anim--back h-5 w-5 text-gray-500 group-hover:text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
            Voltar
          </button>
        </div>
      </div>

      <form class="space-y-6" @submit.prevent="salvarCentroDeCusto">
        <div class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div class="border-b border-gray-100 px-6 py-5">
            <div class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <h2 class="text-lg font-semibold text-gray-900">Informações Gerais</h2>
                <p class="mt-1 max-w-2xl text-sm text-gray-500">Preencha o nome e a descrição do centro e defina se ele deve controlar o limite de envio mensal.</p>
              </div>

              <p class="max-w-md rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-gray-500 shadow-sm">Use nomes claros para facilitar filtros, operação e leitura nas listagens administrativas.</p>
            </div>
          </div>

          <div class="p-6">
            <div class="grid grid-cols-12 gap-6">
              <div class="col-span-7 min-w-0 rounded-3xl border border-gray-100 bg-slate-50/70 p-5 md:p-6">
                <div class="grid grid-cols-1 gap-5">
                  <div class="flex flex-col gap-1.5">
                    <label class="text-sm font-medium text-gray-500"> Nome </label>
                    <input v-model="form.nome" type="text" placeholder="Ex: Financeiro" :class="inputClass" required />
                  </div>

                  <div class="flex flex-col gap-1.5">
                    <label class="text-sm font-medium text-gray-500"> Descrição </label>
                    <textarea v-model="form.descricao" rows="7" placeholder="Descreva rapidamente a finalidade deste centro de custo." :class="textareaClass" />
                  </div>
                </div>
              </div>

              <div class="col-span-5 min-w-0 rounded-3xl border border-gray-200 bg-gradient-to-br from-slate-50 to-white p-5 shadow-sm">
                <div class="border-b border-gray-100 pb-4">
                  <p class="text-xs font-semibold uppercase tracking-[0.18em] text-gray-400">Configuração</p>
                  <h3 class="mt-2 text-lg font-semibold text-gray-900">Regras do centro</h3>
                  <p class="mt-1 text-sm text-gray-500">Ajuste o comportamento operacional deste cadastro.</p>
                </div>

                <div class="mt-5 space-y-4">
                  <label class="flex cursor-pointer gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-4 shadow-sm transition hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-md">
                    <input v-model="form.limiteMensal.ativo" type="checkbox" :class="checkboxClass" />
                    <span class="flex flex-col">
                      <span class="text-sm font-medium text-gray-800"> Limite de envio mensal </span>
                      <span class="mt-1 text-sm leading-6 text-gray-500"> Ative para controlar a capacidade de envios deste centro de custo. </span>
                    </span>
                  </label>

                  <div v-if="form.limiteMensal.ativo" class="rounded-2xl border border-gray-200 bg-white px-4 py-4 shadow-sm">
                    <label class="flex flex-col gap-1.5 text-sm font-medium text-gray-500">
                      Volume máximo
                      <input v-model.number="form.limiteMensal.volume" type="number" min="0" placeholder="0" :class="inputClass" />
                    </label>
                  </div>

                  <div class="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-4 text-sm text-gray-500">
                    {{ form.limiteMensal.ativo ? "O centro será criado com controle mensal habilitado." : "O centro será criado sem limite mensal ativo." }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="feedback.message" class="rounded-2xl border px-4 py-3 text-sm" :class="feedbackClass">
          {{ feedback.message }}
        </div>

        <div class="flex flex-col gap-3 sm:flex-row sm:justify-end">
          <button
            type="submit"
            class="h-11 rounded-xl px-6 font-medium text-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
            :style="{
              backgroundColor: primaryColor
            }"
            @mouseenter="$event.target.style.backgroundColor = mixPrimaryColor('#111827', 0.2)"
            @mouseleave="$event.target.style.backgroundColor = primaryColor"
          >
            Salvar centro de custo
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { colorMixin } from "@/utils/colorMixin";

export default {
  mixins: [colorMixin],
  data() {
    return {
      inputClass: "h-11 rounded-xl border border-gray-200 bg-white px-4 shadow-sm outline-none transition focus:border-blue-300 focus:ring-2 focus:ring-blue-100",
      textareaClass: "rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm outline-none transition focus:border-blue-300 focus:ring-2 focus:ring-blue-100",
      feedback: {
        message: "",
        type: "success"
      },
      form: {
        _id: null,
        nome: "",
        descricao: "",
        limiteMensal: {
          ativo: false,
          volume: 0
        }
      }
    };
  },
  computed: {
    feedbackClass() {
      const feedbackStyles = {
        success: "border-green-200 bg-green-50 text-green-700",
        warning: "border-amber-200 bg-amber-50 text-amber-700"
      };

      return feedbackStyles[this.feedback.type] || feedbackStyles.success;
    }
  },
  watch: {
    "form.limiteMensal.ativo"(enabled) {
      if (!enabled) {
        this.form.limiteMensal.volume = 0;
      }
    }
  },
  methods: {
    mostrarFeedback(message, type = "success") {
      this.feedback.message = message;
      this.feedback.type = type;
    },
    voltar() {
      this.$router.push({ path: "/centros" });
    },
    normalizeForm() {
      this.form.nome = String(this.form.nome || "").trim();
      this.form.descricao = String(this.form.descricao || "").trim();
      this.form.limiteMensal = {
        ativo: !!(this.form.limiteMensal && this.form.limiteMensal.ativo),
        volume: Number(this.form.limiteMensal && this.form.limiteMensal.volume ? this.form.limiteMensal.volume : 0)
      };

      if (!this.form.limiteMensal.ativo) {
        this.form.limiteMensal.volume = 0;
      }
    },
    async salvarCentroDeCusto() {
      if (!this.form.nome.trim()) {
        this.mostrarFeedback("Preencha o nome do centro de custo para continuar.", "warning");
        return;
      }

      this.normalizeForm();

      const method = this.form._id ? "put" : "post";
      const response = await this.$http[method]("/v1/centrocusto", this.form);

      if (response && response.data && response.data.success) {
        this.$vToastify.success("Centro de custo salvo com sucesso!");
        this.$router.push({ path: "/centros" });
        return;
      }

      const errorMessage = response && response.data && response.data.err ? response.data.err : "Não foi possível salvar o centro de custo.";
      this.mostrarFeedback(errorMessage, "warning");
      this.$vToastify.error(errorMessage);
    },
    async carregarCentroDeCusto() {
      const id = this.$route.params.id;
      if (!id) {
        return;
      }

      const response = await this.$http.get(`/v1/centrocusto/${id}`);
      const centro = response && response.data ? response.data : null;

      if (!centro) {
        return;
      }

      this.form = {
        _id: centro._id || null,
        nome: centro.nome || "",
        descricao: centro.descricao || "",
        limiteMensal: {
          ativo: !!(centro.limiteMensal && centro.limiteMensal.ativo),
          volume: centro.limiteMensal && centro.limiteMensal.volume ? centro.limiteMensal.volume : 0
        }
      };
    }
  },
  async beforeMount() {
    await this.carregarCentroDeCusto();
  }
};
</script>
